// src/utils/dashboardDataTransformer.js - PROGRESSIVE LOADING VERSION
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { GRAMMAR_TOPICS_BY_LEVEL, getTopicsForLevel } from '../services/grammarTopicsMap';
import grammarProgressService from '../services/grammarProgressService';

/**
 * PHASE 1: Instant data from userProgress object (no async calls)
 * This loads immediately - no waiting for Firebase or file fetches
 */
export const transformImmediateData = (userProgress) => {
  if (!userProgress) return null;

  const stats = userProgress.stats || {};
  const preferences = userProgress.preferences || {};
  const currentLevel = preferences.currentLevel || 'B1';

  return {
    studyStreak: stats.currentStreak || 0,
    dailyGoal: preferences.dailyGoal || 5,
    todayLessons: 0,

    vocabularyStats: {
      mastered: stats.vocabularyMastered || 0,
      learning: Math.max(0, (stats.vocabularyWordsLearned || 0) - (stats.vocabularyMastered || 0)),
      dueForReview: stats.vocabularyWordsReviewed || 0,
      total: stats.vocabularyWordsLearned || 0
    },

    grammarStats: {
      overallAccuracy: stats.overallAccuracy || 0,
      completedTopics: stats.grammarTopicsCompleted || 0,
      totalTopics: getTopicsForLevel(currentLevel)?.totalTopics || 11,
      topics: [],
      weakAreas: []
    },

    daysSinceLastStudy: calculateDaysSinceLastStudy(userProgress.lastStudyDate),
    nextMilestone: calculateNextMilestone(stats),

    weeklyData: getEmptyWeeklyData(),
    activityData: getEmpty30DayData(),
    vocabularyData: { totalWords: 0, categories: [], mistakeWords: [] },
    mistakesData: { totalMistakes: 0, topicBreakdown: [], recentMistakes: [] },

    skillsData: {
      vocabulary: {
        mastered: stats.vocabularyWordsLearned || 0,
        total: 2000,
        percentage: Math.min(Math.round((stats.vocabularyWordsLearned || 0) / 20), 100)
      },
      grammar: {
        mastered: stats.grammarTopicsCompleted || 0,
        total: getTopicsForLevel(currentLevel)?.totalTopics || 11,
        percentage: Math.min(Math.round(((stats.grammarTopicsCompleted || 0) / 16) * 100), 100)
      },
      retention: {
        rate: stats.overallAccuracy || 0,
        percentage: stats.overallAccuracy || 0
      },
      overall: {
        percentage: calculateOverallProficiency(stats)
      }
    },

    achievementStats: {
      vocabularyMastered: stats.vocabularyWordsLearned || 0,
      grammarTopicsCompleted: 0,
      reviewsCompleted: stats.vocabularyWordsReviewed || 0,
      currentStreak: stats.currentStreak || 0
    },

    streakData: {
      currentStreak: stats.currentStreak || 0,
      longestStreak: stats.longestStreak || 0,
      freezesAvailable: 2,
      repairsAvailable: 1,
      weekendExcluded: false,
      streakHistory: getEmptyStreakHistory()
    }
  };
};

/**
 * PHASE 2: Grammar & Activity data (fast Firestore queries)
 */
export const loadGrammarData = async (userId, userPreferredLevel) => {
  try {
    // Auto-detect actual grammar level with data
    const activeGrammarLevel = await detectActiveGrammarLevel(userId);
const [topics, mistakes, todayCount, weeklyData, continuation] = await Promise.all([
      buildGrammarTopicsArray(userId, activeGrammarLevel),
      buildMistakesData({ userId }, activeGrammarLevel),
      getTodayLessonsCount(userId),
      generateWeeklyData(userId),
      grammarProgressService.getContinuationPoint(userId, activeGrammarLevel)  // NEW
    ]);
    const completedTopics = topics.filter(t => t.mastery >= 80).length;
    const topicsWithMastery = topics.filter(t => t.mastery > 0);
    const overallAccuracy = topicsWithMastery.length > 0
      ? Math.round(topicsWithMastery.reduce((sum, t) => sum + t.mastery, 0) / topicsWithMastery.length)
      : 0;
    return {
      grammarStats: {
        topics,
        weakAreas: await identifyWeakAreas(userId, activeGrammarLevel),
        activeLevel: activeGrammarLevel , // Include detected level
         completedTopics,
        overallAccuracy
      },
         grammarContinuation: continuation?.pillarId ? continuation : null,
      mistakesData: mistakes,
      todayLessons: todayCount,
      weeklyData
    };
  } catch (error) {
    console.error('Error loading grammar data:', error);
    return {
      grammarStats: { topics: [], weakAreas: [], activeLevel: userPreferredLevel },
        grammarContinuation: null,
      mistakesData: { totalMistakes: 0, topicBreakdown: [], recentMistakes: [] },
      todayLessons: 0,
      weeklyData: getEmptyWeeklyData()
    };
  }
};
/**
 * PHASE 3: Vocabulary data (slow - file + SRS lookups)
 */
export const loadVocabularyData = async (userId, userPreferredLevel, userLanguage) => {
  // Auto-detect actual vocab level with files
  const activeVocabLevel = await detectActiveVocabLevel(userLanguage);

  console.log(`📖 User preferred level: ${userPreferredLevel}, Active vocab level: ${activeVocabLevel}`);

  return await buildVocabularyCategories(userId, activeVocabLevel, userLanguage);
};
/**
 * PHASE 4: Activity calendar (medium speed)
 */
export const loadActivityData = async (userId) => {
  try {
    const [activityData, streakHistory] = await Promise.all([
      generateActivityData(userId),
      generateStreakHistory(userId)
    ]);

    return {
      activityData,
      streakData: {
        streakHistory
      }
    };
  } catch (error) {
    console.error('Error loading activity data:', error);
    return {
      activityData: getEmpty30DayData(),
      streakData: { streakHistory: getEmptyStreakHistory() }
    };
  }
};

// ============================================================================
// ALL HELPER FUNCTIONS BELOW (keep as-is)
// ============================================================================

const generateWeeklyData = async (userId) => {
  if (!userId) return getEmptyWeeklyData();

  try {
    // Get study sessions (lessons, exams, etc.)
    const sessionsRef = collection(db, 'users', userId, 'studySessions');
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0);

    const q = query(
      sessionsRef,
      where('createdAt', '>=', Timestamp.fromDate(sevenDaysAgo)),
      orderBy('createdAt', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const dailyGoal = 5;
    const weekData = {};

    days.forEach(day => {
      weekData[day] = { lessons: 0, minutes: 0, goalMet: false };
    });

    // Process study sessions
    querySnapshot.forEach((doc) => {
      const session = doc.data();
      const sessionDate = session.createdAt?.toDate() || new Date();
      const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][sessionDate.getDay()];

      if (weekData[dayName]) {
        weekData[dayName].lessons += session.completed ? 1 : 0;
        weekData[dayName].minutes += session.duration || 0;
      }
    });

    // Get total website time (any activity on the website)
    try {
      const websiteSessionsRef = collection(db, 'users', userId, 'websiteSessions');
      const websiteQ = query(
        websiteSessionsRef,
        where('date', '>=', Timestamp.fromDate(sevenDaysAgo))
      );

      const websiteSnapshot = await getDocs(websiteQ);
      
      websiteSnapshot.forEach((doc) => {
        const session = doc.data();
        const sessionDate = session.date?.toDate() || session.startTime?.toDate() || new Date();
        const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][sessionDate.getDay()];

        if (weekData[dayName]) {
          // Add website time to total minutes (this includes ALL time on site)
          weekData[dayName].minutes += session.duration || 0;
        }
      });
    } catch (websiteError) {
      console.warn('Could not fetch website sessions (may not exist yet):', websiteError);
      // Continue without website time - it's okay if this collection doesn't exist yet
    }

    Object.keys(weekData).forEach(day => {
      weekData[day].goalMet = weekData[day].lessons >= dailyGoal;
    });

    return days.map(day => ({
      day,
      lessons: weekData[day].lessons,
      minutes: weekData[day].minutes,
      goalMet: weekData[day].goalMet
    }));

  } catch (error) {
    console.error('Error generating weekly data:', error);
    return getEmptyWeeklyData();
  }
};

const generateActivityData = async (userId) => {
  if (!userId) return getEmpty30DayData();

  try {
    const sessionsRef = collection(db, 'users', userId, 'studySessions');
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);

    const q = query(
      sessionsRef,
      where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo)),
      orderBy('createdAt', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const activityMap = {};
    const today = new Date();

    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      activityMap[dateStr] = { minutes: 0, sessions: 0 };
    }

    querySnapshot.forEach((doc) => {
      const session = doc.data();
      const sessionDate = session.createdAt?.toDate() || new Date();
      const dateStr = sessionDate.toISOString().split('T')[0];

      if (activityMap[dateStr]) {
        activityMap[dateStr].minutes += session.duration || 0;
        activityMap[dateStr].sessions += 1;
      }
    });

    return Object.entries(activityMap)
      .map(([date, data]) => ({
        date,
        minutes: data.minutes,
        sessions: data.sessions
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

  } catch (error) {
    console.error('Error generating activity data:', error);
    return getEmpty30DayData();
  }
};

const getTodayLessonsCount = async (userId) => {
  if (!userId) return 0;

  try {
    const sessionsRef = collection(db, 'users', userId, 'studySessions');
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const q = query(
      sessionsRef,
      where('createdAt', '>=', Timestamp.fromDate(today))
    );

    const querySnapshot = await getDocs(q);
    let count = 0;
    querySnapshot.forEach(doc => {
      if (doc.data().completed === true) {
        count++;
      }
    });

    return count;

  } catch (error) {
    console.error('Error getting today\'s lessons:', error);
    return 0;
  }
};

const buildGrammarTopicsArray = async (userId, level) => {
  const levelData = getTopicsForLevel(level);
  if (!levelData || !userId) return [];

  try {
    const progress = await grammarProgressService.getUserProgress(userId, level);

    if (!progress) {
      return levelData.topics.map(topic => ({
        id: topic.id,
        name: topic.name,
        category: topic.category,
        mastery: 0
      }));
    }

    return levelData.topics.map(topic => {
      const pillarId = topic.id;
      const pillarProgress = progress.pillars?.[pillarId];

      let mastery = 0;

      if (pillarProgress && pillarProgress.exercises) {
        const exercises = Object.values(pillarProgress.exercises);
        const completed = exercises.filter(ex => ex.completed && ex.correct).length;
        const total = exercises.length;

        if (total > 0) {
          mastery = Math.round((completed / total) * 100);
        }
      }

      return {
        id: topic.id,
        name: topic.name,
        category: topic.category,
        mastery: mastery
      };
    });

  } catch (error) {
    console.error('Error building grammar topics:', error);
    return levelData.topics.map(topic => ({
      id: topic.id,
      name: topic.name,
      category: topic.category,
      mastery: 0
    }));
  }
};

const buildVocabularyCategories = async (userId, level, userLanguage = 'en') => {
  if (!userId || !level) {
    return {
      totalWords: 0,
      categories: [],
      mistakeWords: [],
      sampleWord: null
    };
  }

  try {
    const levelLower = level.toLowerCase();
    const languageCodes = {
      'english': 'en', 'spanish': 'es', 'french': 'fr',
      'turkish': 'tr', 'russian': 'ru', 'german': 'de',
      'en': 'en', 'es': 'es', 'fr': 'fr', 'tr': 'tr', 'ru': 'ru', 'de': 'de'
    };

    const langCode = languageCodes[userLanguage.toLowerCase()] || 'en';
    const isJsonResponse = (r) => r.ok && (r.headers.get('content-type') || '').includes('json');

    let vocabUrl = `/data/vocabulary/vocabulary-${levelLower}_${langCode}.json`;
    let response = await fetch(vocabUrl);

    if (!isJsonResponse(response) && langCode !== 'en') {
      vocabUrl = `/data/vocabulary/vocabulary-${levelLower}_en.json`;
      response = await fetch(vocabUrl);
    }

    if (!isJsonResponse(response)) {
      return { totalWords: 0, categories: [], mistakeWords: [] };
    }

    const vocabData = await response.json();

    if (!vocabData || !vocabData.categories) {
      return { totalWords: 0, categories: [], mistakeWords: [], sampleWord: null };
    }

    const categories = [];
    let totalWords = 0;
    const mistakeWords = [];
    let sampleWord = null;

    for (const [categoryKey, category] of Object.entries(vocabData.categories || {})) {
      let categoryWords = 0;
      let masteredWords = 0;
      let learningWords = 0;
      let newWords = 0;

      for (const [subcatKey, subcat] of Object.entries(category.subcategories || {})) {
        const subcatId = `${categoryKey}_${subcatKey}`;
        const cards = subcat.cards || [];
        categoryWords += cards.length;

        // Capture first available vocab card as a sample for preview when there are no SRS-driven mistakes yet
        if (!sampleWord && cards.length > 0) {
          const firstCard = cards[0];
          sampleWord = {
            word: firstCard.german || firstCard.front,
            translation: firstCard.english || firstCard.back,
            category: category.name
          };
        }

        try {
          const srsData = await loadSRSDataForSubcategory(userId, subcatId);

          cards.forEach(card => {
            const cardData = srsData[card.id];
            if (cardData) {
              const state = cardData.state;
              if (state === 'review' && cardData.interval >= 21) {
                masteredWords++;
              } else if (state === 'learning' || state === 'review') {
                learningWords++;
              } else {
                newWords++;
              }

              if (cardData.lapses >= 3) {
                mistakeWords.push({
                  word: card.german || card.front,
                  translation: card.english || card.back,
                  category: category.name,
                  lapses: cardData.lapses
                });
              }
            } else {
              newWords++;
            }
          });
        } catch (error) {
          newWords += cards.length;
        }
      }

      totalWords += categoryWords;

      categories.push({
        id: categoryKey,
        name: category.name,
        icon: category.icon || '📚',
        wordCount: categoryWords,
        masteredCount: masteredWords,
        learningCount: learningWords,
        newCount: newWords,
        percentage: categoryWords > 0 ? Math.round((masteredWords / categoryWords) * 100) : 0
      });
    }

    mistakeWords.sort((a, b) => b.lapses - a.lapses);

    return {
      totalWords: totalWords,
      categories: categories,
      mistakeWords: mistakeWords.slice(0, 10),
      sampleWord
    };

  } catch (error) {
    console.error('Error building vocabulary categories:', error);
    return {
      totalWords: 0,
      categories: [],
      mistakeWords: [],
      sampleWord: null
    };
  }
};

const loadSRSDataForSubcategory = async (userId, subcategoryId) => {
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const srsRef = doc(db, `users/${userId}/srs/${subcategoryId}`);
    const srsDoc = await getDoc(srsRef);

    if (srsDoc.exists()) {
      return srsDoc.data();
    }

    return {};
  } catch (error) {
    return {};
  }
};

const buildMistakesData = async (userProgress, currentLevel) => {
  const userId = userProgress?.userId || userProgress?.uid;

  if (!userId) {
    return { totalMistakes: 0, topicBreakdown: [], recentMistakes: [] };
  }

  try {
    const levels = ['A1', 'A2', 'B1', 'B2', 'C1'];
    let allMistakes = [];
    let allWeakAreas = [];

    for (const level of levels) {
      try {
        const mistakes = await grammarProgressService.getMistakes(userId, level);
        const weakAreas = await grammarProgressService.getWeakAreas(userId, level, 10);

        if (mistakes.length > 0) {
          allMistakes = [...allMistakes, ...mistakes.map(m => ({ ...m, level }))];
        }

        if (weakAreas.length > 0) {
          allWeakAreas = [...allWeakAreas, ...weakAreas.map(w => ({ ...w, level }))];
        }
      } catch (error) {
        // Silent fail for individual levels
      }
    }

    let topicBreakdown = [];

    if (allWeakAreas.length > 0) {
      allWeakAreas.sort((a, b) => b.mistakeCount - a.mistakeCount);
      topicBreakdown = allWeakAreas.slice(0, 10).map(area => ({
        id: area.pillarId,
        name: formatPillarName(area.pillarId),
        category: getCategoryForPillar(area.pillarId, area.level),
        level: area.level,
        mistakes: area.mistakeCount,
        accuracy: 0
      }));
    } else if (allMistakes.length > 0) {
      const mistakesByPillar = {};

      allMistakes.forEach(mistake => {
        const pillarId = mistake.pillarId || mistake.exerciseId?.split('_')[0] || 'unknown';
        const level = mistake.level || currentLevel;
        const key = `${pillarId}_${level}`;

        if (!mistakesByPillar[key]) {
          mistakesByPillar[key] = { pillarId, level, count: 0 };
        }

        mistakesByPillar[key].count++;
      });

      topicBreakdown = Object.values(mistakesByPillar)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)
        .map(item => ({
          id: item.pillarId,
          name: formatPillarName(item.pillarId),
          category: getCategoryForPillar(item.pillarId, item.level),
          level: item.level,
          mistakes: item.count,
          accuracy: 0
        }));
    }

    allMistakes.sort((a, b) => {
      const timeA = a.timestamp?.seconds || a.incorrectAt?.seconds || 0;
      const timeB = b.timestamp?.seconds || b.incorrectAt?.seconds || 0;
      return timeB - timeA;
    });

    const recentMistakes = allMistakes
      .slice(0, 5)
      .map(mistake => ({
        topicName: formatPillarName(mistake.pillarId || mistake.exerciseId?.split('_')[0]),
        question: `Exercise: ${mistake.exerciseId}`,
        level: mistake.level,
        timestamp: mistake.timestamp || mistake.incorrectAt,
        userAnswer: mistake.userAnswer,
        correctAnswer: mistake.correctAnswer
      }));

    return {
      totalMistakes: allMistakes.length,
      topicBreakdown,
      recentMistakes
    };

  } catch (error) {
    console.error('Error building mistakes data:', error);
    return { totalMistakes: 0, topicBreakdown: [], recentMistakes: [] };
  }
};

const identifyWeakAreas = async (userId, level) => {
  if (!userId) return [];

  try {
    const weakAreas = await grammarProgressService.getWeakAreas(userId, level, 3);
    return weakAreas.map(area => ({
      pillarId: area.pillarId,
      mistakeCount: area.mistakeCount,
      name: formatPillarName(area.pillarId)
    }));
  } catch (error) {
    return [];
  }
};

const generateStreakHistory = async (userId) => {
  if (!userId) return getEmptyStreakHistory();

  try {
    const weeklyData = await generateWeeklyData(userId);
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return days.map((day, index) => ({
      day,
      completed: weeklyData[index]?.lessons > 0,
      freezeUsed: false
    }));
  } catch (error) {
    return getEmptyStreakHistory();
  }
};

const calculateDaysSinceLastStudy = (lastStudyDate) => {
  if (!lastStudyDate) return 0;

  const lastDate = lastStudyDate.seconds
    ? new Date(lastStudyDate.seconds * 1000)
    : new Date(lastStudyDate);

  const now = new Date();
  const diffTime = Math.abs(now - lastDate);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

const calculateNextMilestone = (stats) => {
  const vocabCount = stats.vocabularyWordsLearned || 0;

  const milestones = [
    { threshold: 50, name: 'Word Starter', type: 'words' },
    { threshold: 100, name: 'Word Collector', type: 'words' },
    { threshold: 500, name: 'Word Scholar', type: 'words' },
    { threshold: 1000, name: 'Word Master', type: 'words' },
    { threshold: 2000, name: 'Word Legend', type: 'words' }
  ];

  const nextMilestone = milestones.find(m => m.threshold > vocabCount);

  if (nextMilestone) {
    return {
      name: nextMilestone.name,
      type: nextMilestone.type,
      remaining: nextMilestone.threshold - vocabCount,
      progress: Math.round((vocabCount / nextMilestone.threshold) * 100),
      path: '/vocabulary'
    };
  }

  return null;
};

const formatPillarName = (pillarId) => {
  if (!pillarId || typeof pillarId !== 'string') return 'Unknown Topic';

  // Search all levels in grammarTopicsMap for this pillarId
  for (const [levelKey, levelData] of Object.entries(GRAMMAR_TOPICS_BY_LEVEL)) {
    const topic = levelData.topics.find(t => t.id === pillarId);
    if (topic) {
      return topic.name;
    }
  }

  // Fallback: format the ID if not found
  return pillarId
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getCategoryForPillar = (pillarId, level) => {
  if (!pillarId || typeof pillarId !== 'string') return 'Grammar';

  // Search grammarTopicsMap for this pillarId's category
  for (const [levelKey, levelData] of Object.entries(GRAMMAR_TOPICS_BY_LEVEL)) {
    const topic = levelData.topics.find(t => t.id === pillarId);
    if (topic) {
      return topic.category;
    }
  }

  // Fallback
  return 'Grammar';
};
const detectActiveGrammarLevel = async (userId) => {
  const levels = ['A2', 'B1', 'B2', 'C1'];

  for (const level of levels) {
    try {
      const progress = await grammarProgressService.getUserProgress(userId, level);
      if (progress?.stats?.completedExercises > 0) {
        return level; // Return first level with actual practice data
      }
    } catch (error) {
      // Continue to next level
    }
  }

  return 'B1'; // Default fallback
};

/**
 * Auto-detect which vocabulary level has files/data
 */
const detectActiveVocabLevel = async (userLanguage) => {
  const levels = ['A1', 'A2', 'B1', 'B2', 'C1'];
  const langCode = userLanguage || 'en';

  for (const level of levels) {
    try {
      const url = `/data/vocabulary/vocabulary-${level.toLowerCase()}_${langCode}.json`;
      const response = await fetch(url);
      if (response.ok) {
        return level; // Return first level with actual vocab files
      }
    } catch (error) {
      // Continue to next level
    }
  }

  return 'A1'; // Default fallback
};
const calculateOverallProficiency = (stats) => {
  const vocabScore = Math.min((stats.vocabularyWordsLearned || 0) / 20, 100);
  const grammarScore = Math.min(((stats.grammarTopicsCompleted || 0) / 16) * 100, 100);
  const accuracyScore = stats.overallAccuracy || 0;

  return Math.round((vocabScore + grammarScore + accuracyScore) / 3);
};

const getEmptyWeeklyData = () => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return days.map(day => ({ day, lessons: 0, minutes: 0, goalMet: false }));
};

const getEmpty30DayData = () => {
  const data = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    data.push({
      date: date.toISOString().split('T')[0],
      minutes: 0,
      sessions: 0
    });
  }
  return data;
};

const getEmptyStreakHistory = () => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return days.map(day => ({ day, completed: false, freezeUsed: false }));
};